import React, { useState, useRef, useEffect } from 'react';
import { Plus, Mail, Eye, Send, Edit3, Trash2, Copy, X, Loader2, Sparkles } from 'lucide-react';
import TokenPurchaseModal from '../../components/TokenPurchaseModal';
import EmailTemplateBuilder from '../../components/EmailTemplateBuilder';
import { emailTemplateService, EmailTemplate } from '../../services/emailTemplateService';
import { renderEmailTemplateToHtml } from '../../lib/emailTemplateRenderer';
import { useBusiness } from '../../contexts/BusinessContext';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import {
  applyBrandColorsToEmailTemplateSections,
  formatBrandGuidelinesForPrompt,
  getBrandGuidelines,
  resolveDefaultOnlineBookingUrl,
} from '../../lib/brandGuidelines';
import { gatherEmailTemplateAiImages } from '../../lib/emailTemplateAiImages';

function TemplateThumb({ html }: { html: string }) {
  const frameRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.25);

  useEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    const update = () => setScale(el.clientWidth / 640);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={frameRef} className="aspect-[4/3] bg-gray-100 overflow-hidden relative">
      <iframe
        title="Email preview"
        sandbox=""
        srcDoc={html}
        tabIndex={-1}
        className="absolute top-0 left-0 border-0 pointer-events-none bg-white"
        style={{ width: 640, height: 860, transform: `scale(${scale})`, transformOrigin: 'top left' }}
      />
    </div>
  );
}

export default function EmailTemplates() {
  const { businessId, selectedBusiness } = useBusiness();
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [templateToSend, setTemplateToSend] = useState<EmailTemplate | null>(null);
  const [testEmail, setTestEmail] = useState('');
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [templateToDuplicate, setTemplateToDuplicate] = useState<EmailTemplate | null>(null);
  const [duplicateName, setDuplicateName] = useState('');
  const [isDuplicating, setIsDuplicating] = useState(false);
  // AI template creation
  const [showAIPromptModal, setShowAIPromptModal] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [isAIGenerating, setIsAIGenerating] = useState(false);
  const [showTokenPurchaseModal, setShowTokenPurchaseModal] = useState(false);
  const [kindPicker, setKindPicker] = useState<null | 'manual' | 'ai'>(null);
  const [createOneOff, setCreateOneOff] = useState(false);
  const aiPromptInputRef = useRef<HTMLTextAreaElement>(null);

  // Get business logo URL for template previews
  const logoUrl = selectedBusiness?.booking_settings?.logo_url;

  // Load templates on mount
  useEffect(() => {
    if (businessId) {
      loadTemplates();
    }
  }, [businessId]);

  const loadTemplates = async () => {
    if (!businessId) return;
    
    setIsLoading(true);
    try {
      const data = await emailTemplateService.getTemplates(businessId);
      setTemplates(data);
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateTemplate = () => {
    setKindPicker('manual');
  };

  const chooseKind = (oneOff: boolean) => {
    const intent = kindPicker;
    setCreateOneOff(oneOff);
    setKindPicker(null);
    if (intent === 'manual') {
      setEditingTemplate({ one_off: oneOff } as any);
      setIsBuilderOpen(true);
      return;
    }
    setShowAIPromptModal(true);
    setTimeout(() => aiPromptInputRef.current?.focus(), 100);
  };

  const handleAICreate = async () => {
    if (!aiPrompt.trim() || !businessId) return;

    setIsAIGenerating(true);
    setShowAIPromptModal(false);

    // Open the builder with a temporary "loading" template
    setEditingTemplate({
      id: undefined,
      name: 'AI Generated Template',
      content: { subject: '', preheader: '', sections: [] },
      one_off: createOneOff,
      _aiLoading: true,
    } as any);
    setIsBuilderOpen(true);

    try {
      let brandGuidelines = '';
      let brandButtonColor = '';
      let brandHeaderColor = '';
      let brandPrimaryColor = '';
      try {
        const guidelines = await getBrandGuidelines(businessId);
        brandButtonColor = (guidelines?.button_color || '').trim();
        brandHeaderColor = (guidelines?.header_color || '').trim();
        brandPrimaryColor = (guidelines?.primary_color || '').trim();
        let onlineBookingUrlFallback: string | undefined;
        if (!(guidelines?.online_booking_url || '').trim()) {
          onlineBookingUrlFallback =
            (await resolveDefaultOnlineBookingUrl(businessId, selectedBusiness?.slug)) ||
            undefined;
        }
        brandGuidelines = formatBrandGuidelinesForPrompt(guidelines, {
          logoUrl,
          websiteUrlFallback: selectedBusiness?.nexus_website_url,
          onlineBookingUrlFallback,
        });
      } catch (err) {
        console.warn('Could not load brand guidelines for AI template create:', err);
      }

      let availableImages: {
        id: string;
        url: string;
        label: string;
        source: string;
      }[] = [];
      try {
        availableImages = await gatherEmailTemplateAiImages(businessId);
      } catch (err) {
        console.warn('Could not gather images for AI template create:', err);
      }

      const { data, error } = await supabase.functions.invoke('ai-create-email-template', {
        body: {
          prompt: aiPrompt.trim(),
          businessId,
          businessName: selectedBusiness?.name || '',
          brandGuidelines: brandGuidelines || undefined,
          brandButtonColor: brandButtonColor || brandPrimaryColor || undefined,
          brandHeaderColor: brandHeaderColor || undefined,
          availableImages,
        }
      });

      if (error) {
        const ctx = (error as any)?.context;
        let detail = error.message || 'Failed to generate template';
        try {
          if (ctx && typeof ctx.json === 'function') {
            const body = await ctx.json();
            if (body?.error) detail = body.error;
          }
        } catch {
          /* ignore */
        }
        throw new Error(detail);
      }

      if (data?.insufficientTokens) {
        setIsBuilderOpen(false);
        setEditingTemplate(null);
        setShowTokenPurchaseModal(true);
        setIsAIGenerating(false);
        return;
      }

      if (!data?.success || !data?.template) {
        throw new Error(data?.error || 'Failed to generate template');
      }

      const generated = data.template;
      const sections = applyBrandColorsToEmailTemplateSections(generated.sections || [], {
        buttonColor: brandButtonColor,
        headerColor: brandHeaderColor,
        primaryColor: brandPrimaryColor,
      });
      setEditingTemplate({
        id: undefined,
        name: generated.name || 'AI Generated Template',
        one_off: createOneOff,
        content: {
          subject: generated.subject || '',
          preheader: generated.preheader || '',
          sections,
        },
      } as any);

      toast.success(`Template created! (${data.tokensUsed} tokens used)`);
    } catch (err: any) {
      console.error('AI template generation error:', err);
      toast.error(err.message || 'Failed to generate template');
      setIsBuilderOpen(false);
      setEditingTemplate(null);
    } finally {
      setIsAIGenerating(false);
      setAiPrompt('');
    }
  };

  const handleEditTemplate = (template: EmailTemplate) => {
    setEditingTemplate(template);
    setIsBuilderOpen(true);
  };

  const handleSaveTemplate = async (template: any) => {
    if (!businessId) return;
    
    setIsSaving(true);
    try {
      // Only update when we have a real saved template id. AI-generated (and blank)
      // templates have no id yet, so they must be created — otherwise we'd PATCH
      // with id "undefined" and Postgres rejects it as an invalid uuid.
      const existingId = editingTemplate?.id;
      if (existingId) {
        // Update existing template
        await emailTemplateService.updateTemplate(existingId, {
          name: template.name,
          content: template.content,
          category: template.category || 'Custom',
          status: template.status || 'draft',
          one_off: template.one_off === true,
        });
      } else {
        // Create new template
        await emailTemplateService.createTemplate(businessId, {
          name: template.name,
          content: template.content,
          category: template.category || 'Custom',
          status: 'draft',
          template_type: template.template_type,
          one_off: template.one_off === true,
        });
      }
      
      // Reload templates
      await loadTemplates();
      setIsBuilderOpen(false);
      setEditingTemplate(null);
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Failed to save template. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (!confirm('Are you sure you want to delete this template?')) return;
    const previous = templates;
    setTemplates((current) => current.filter((template) => template.id !== templateId));
    try {
      await emailTemplateService.deleteTemplate(templateId);
    } catch (error) {
      console.error('Error deleting template:', error);
      setTemplates(previous);
      alert('Failed to delete template. Please try again.');
    }
  };

  const handleDuplicateTemplate = (template: EmailTemplate) => {
    setTemplateToDuplicate(template);
    setDuplicateName(`${template.name} (Copy)`);
    setShowDuplicateModal(true);
  };

  const handleConfirmDuplicate = async () => {
    if (!businessId || !templateToDuplicate || !duplicateName.trim()) return;
    
    setIsDuplicating(true);
    try {
      // Create a new template with the same content but new name
      await emailTemplateService.createTemplate(businessId, {
        name: duplicateName.trim(),
        content: templateToDuplicate.content,
        category: templateToDuplicate.category || 'Custom',
        status: 'draft',
        template_type: templateToDuplicate.template_type,
        one_off: templateToDuplicate.one_off === true,
        subject: templateToDuplicate.subject,
        preheader: templateToDuplicate.preheader,
        variables: templateToDuplicate.variables,
        tags: templateToDuplicate.tags
      });
      
      // Reload templates
      await loadTemplates();
      
      // Close modal and reset state
      setShowDuplicateModal(false);
      setTemplateToDuplicate(null);
      setDuplicateName('');
    } catch (error) {
      console.error('Error duplicating template:', error);
      alert('Failed to duplicate template. Please try again.');
    } finally {
      setIsDuplicating(false);
    }
  };

  const handleMoveTemplate = async (template: EmailTemplate, oneOff: boolean) => {
    setTemplates((current) => current.map((item) => (item.id === template.id ? { ...item, one_off: oneOff } : item)));
    try {
      await emailTemplateService.updateTemplate(template.id, { one_off: oneOff });
    } catch (error) {
      console.error('Error moving template:', error);
      setTemplates((current) => current.map((item) => (item.id === template.id ? template : item)));
      alert('Could not move that template. Please try again.');
    }
  };

  const handleSendTest = (template: EmailTemplate) => {
    setTemplateToSend(template);
    setTestEmail('');
    setShowSendModal(true);
  };

  const handleSendTestEmail = async () => {
    if (!testEmail.trim() || !templateToSend || !businessId) return;

    setIsSendingTest(true);
    try {
      let htmlContent = '';
      
      if (templateToSend.content && templateToSend.content.sections) {
        htmlContent = renderEmailTemplateToHtml({
          sections: templateToSend.content.sections,
          businessName: selectedBusiness?.name || 'Your Business',
          logoUrl: selectedBusiness?.booking_settings?.logo_url,
        });
      } else {
        htmlContent = '<p>Template content not available</p>';
      }

      const { data, error: invokeError } = await supabase.functions.invoke('gmail-send-email', {
        body: {
          to: testEmail.trim(),
          subject: templateToSend.subject || 'Test Email Template',
          body: htmlContent,
          businessId: businessId
        }
      });

      console.log('📧 Email send response:', { data, invokeError });

      if (invokeError) {
        console.error('❌ Supabase function invocation error:', invokeError);
        throw new Error(`Function error: ${invokeError.message || 'Unknown error'}`);
      }

      if (data?.success) {
        alert('Test email sent successfully!');
        setShowSendModal(false);
        setTestEmail('');
        setTemplateToSend(null);
      } else {
        const errorMessage = data?.error || 'Failed to send email';
        console.error('❌ Email send failed:', errorMessage);
        throw new Error(errorMessage);
      }

    } catch (error: any) {
      console.error('❌ Error sending test email:', error);
      alert(`Failed to send test email: ${error.message || 'Please try again.'}`);
    } finally {
      setIsSendingTest(false);
    }
  };

  const savedTemplates = templates.filter((template) => !template.one_off);
  const oneOffTemplates = templates.filter((template) => template.one_off);

  return (
    <>
      <div className="bg-gray-50">
        <div className="w-full">

          {/* Templates Container */}
          <div className="bg-white shadow-sm border-t border-gray-200 overflow-hidden">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Your Designs</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setKindPicker('ai')}
                  className="px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 active:bg-blue-800 transition-all flex items-center space-x-2 shadow-sm font-medium text-sm touch-manipulation"
                  style={{ minHeight: '44px' }}
                >
                  <Sparkles className="h-4 w-4" />
                  <span>AI Create</span>
                </button>
                <button
                  onClick={handleCreateTemplate}
                  className="px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 active:bg-blue-800 transition-colors flex items-center space-x-2 shadow-sm font-medium text-sm touch-manipulation"
                  style={{ minHeight: '44px' }}
                >
                  <Plus className="h-4 w-4" />
                  <span>Create Design</span>
                </button>
              </div>
            </div>
            
            <div className="p-4 sm:p-6">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
                </div>
              ) : templates.length > 0 ? (
                <div className="space-y-10">
                  {([
                    { title: 'Templates', hint: 'Saved to use again on campaigns and emails.', items: savedTemplates, empty: 'No saved templates yet.' },
                    { title: 'One-offs', hint: 'Written for one person, including emails Nexus drafts.', items: oneOffTemplates, empty: 'No one-offs yet.' },
                  ] as const).map((section) => (
                    <section key={section.title}>
                      <div className="mb-4">
                        <h3 className="text-base font-semibold text-gray-900">{section.title}</h3>
                        <p className="text-sm text-gray-500">{section.hint}</p>
                      </div>
                      {section.items.length === 0 ? (
                        <p className="text-sm text-gray-400">{section.empty}</p>
                      ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 2xl:grid-cols-6 gap-3">
                  {section.items.map((template) => {
                  const previewHtml = template.content?.sections?.length
                    ? renderEmailTemplateToHtml({
                        sections: template.content.sections,
                        businessName: selectedBusiness?.name || 'Your Business',
                        logoUrl,
                      })
                    : '';
                  return (
                  <div
                    key={template.id}
                    onClick={() => {
                      setEditingTemplate(template);
                      setIsBuilderOpen(true);
                    }}
                    className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-md hover:border-blue-200 transition-colors cursor-pointer touch-manipulation"
                  >
                    {previewHtml ? (
                      <TemplateThumb html={previewHtml} />
                    ) : (
                    <div className="aspect-[4/3] bg-gray-50 flex items-center justify-center px-3">
                      <div className="text-center">
                        <Mail className="h-5 w-5 text-gray-300 mx-auto mb-2" />
                        <div className="text-[11px] font-medium text-gray-500 line-clamp-3">{template.subject || template.name}</div>
                      </div>
                    </div>
                    )}
                    <div className="p-2 border-t border-gray-100">
                      <div className="text-xs font-medium text-gray-900 truncate" title={template.name}>
                        {template.name}
                      </div>
                      <div className="mt-1.5 flex items-center gap-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setTemplateToSend(template);
                            setShowSendModal(true);
                          }}
                          className="flex-1 min-w-0 px-2 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-[11px] font-medium"
                          title="Send a test of this email"
                        >
                          Test send
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDuplicateTemplate(template);
                          }}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                          title="Duplicate"
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteTemplate(template.id);
                          }}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                          title="Delete"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMoveTemplate(template, !template.one_off);
                        }}
                        className="mt-1 text-[10px] font-medium text-gray-400 hover:text-blue-600"
                      >
                        {template.one_off ? 'Save as template' : 'Move to one-offs'}
                      </button>
                    </div>
                  </div>
                  );
                  })}
                </div>
                      )}
                    </section>
                  ))}
                </div>
              ) : (
                /* Empty State */
                <div className="text-center py-12 sm:py-16 px-4">
                  <div className="max-w-md mx-auto">
                    <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-blue-200 rounded-2xl flex items-center justify-center mx-auto mb-6">
                      <Mail className="h-10 w-10 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">No email templates yet</h3>
                    <p className="text-gray-600 mb-8 leading-relaxed text-sm sm:text-base">Create your first email template to streamline your communications and save time on future campaigns.</p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                      <button
                        onClick={() => setKindPicker('ai')}
                        className="px-8 py-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 active:bg-blue-800 transition-all shadow-lg font-semibold touch-manipulation flex items-center space-x-2"
                        style={{ minHeight: '50px' }}
                      >
                        <Sparkles className="h-5 w-5" />
                        <span>Create with AI</span>
                      </button>
                      <button
                        onClick={handleCreateTemplate}
                        className="px-8 py-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 active:bg-blue-800 transition-colors shadow-lg font-semibold touch-manipulation flex items-center space-x-2"
                        style={{ minHeight: '50px' }}
                      >
                        <Plus className="h-5 w-5" />
                        <span>Create Manually</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Email Template Builder Modal */}
      <EmailTemplateBuilder
        isOpen={isBuilderOpen}
        onClose={() => {
          setIsBuilderOpen(false);
          setEditingTemplate(null);
        }}
        onSave={handleSaveTemplate}
        initialTemplate={editingTemplate}
      />

      {/* Duplicate Template Modal */}
      {showDuplicateModal && templateToDuplicate && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center sm:p-4"
          style={{
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'env(safe-area-inset-bottom)',
            paddingLeft: 'env(safe-area-inset-left)',
            paddingRight: 'env(safe-area-inset-right)'
          }}
          onClick={() => {
            setShowDuplicateModal(false);
            setTemplateToDuplicate(null);
            setDuplicateName('');
          }}
        >
          <div 
            className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl max-w-md w-full p-5 sm:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drag indicator for mobile */}
            <div className="sm:hidden w-10 h-1 bg-gray-300 rounded-full mx-auto mb-4" />
            
            <div className="flex items-center justify-between mb-5 sm:mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Copy className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">Duplicate Template</h3>
                  <p className="text-xs sm:text-sm text-gray-500 truncate">Copy of: {templateToDuplicate.name}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowDuplicateModal(false);
                  setTemplateToDuplicate(null);
                  setDuplicateName('');
                }}
                className="p-2 hover:bg-gray-100 active:bg-gray-200 rounded-xl transition-colors touch-manipulation"
              >
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>

            <div className="mb-5 sm:mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Template Name
              </label>
              <input
                type="text"
                value={duplicateName}
                onChange={(e) => setDuplicateName(e.target.value)}
                placeholder="Enter template name"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent touch-manipulation"
                style={{ fontSize: '16px', minHeight: '48px' }}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && duplicateName.trim()) {
                    handleConfirmDuplicate();
                  }
                }}
              />
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowDuplicateModal(false);
                  setTemplateToDuplicate(null);
                  setDuplicateName('');
                }}
                className="flex-1 px-4 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 rounded-xl transition-colors font-medium touch-manipulation"
                style={{ minHeight: '48px' }}
                disabled={isDuplicating}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDuplicate}
                disabled={!duplicateName.trim() || isDuplicating}
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 active:bg-blue-800 disabled:bg-blue-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2 font-medium touch-manipulation"
                style={{ minHeight: '48px' }}
              >
                {isDuplicating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Duplicating...</span>
                  </>
                ) : (
                  <span>Duplicate</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Test Email Modal */}
      {showSendModal && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center sm:p-4"
          style={{
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'env(safe-area-inset-bottom)',
            paddingLeft: 'env(safe-area-inset-left)',
            paddingRight: 'env(safe-area-inset-right)'
          }}
          onClick={() => {
            if (!isSendingTest) {
              setShowSendModal(false);
              setTestEmail('');
              setTemplateToSend(null);
            }
          }}
        >
          <div 
            className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl max-w-md w-full p-5 sm:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drag indicator for mobile */}
            <div className="sm:hidden w-10 h-1 bg-gray-300 rounded-full mx-auto mb-4" />
            
            <div className="flex items-center space-x-3 mb-5 sm:mb-6">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Send className="h-5 w-5 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">Send Test Email</h3>
                <p className="text-xs sm:text-sm text-gray-500 truncate">Template: {templateToSend?.name}</p>
              </div>
            </div>

            <div className="mb-5 sm:mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="Enter email address to test..."
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent touch-manipulation"
                style={{ minHeight: '48px', fontSize: '16px' }}
                disabled={isSendingTest}
              />
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowSendModal(false);
                  setTestEmail('');
                  setTemplateToSend(null);
                }}
                className="flex-1 px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 rounded-xl transition-colors font-medium touch-manipulation"
                style={{ minHeight: '48px' }}
                disabled={isSendingTest}
              >
                Cancel
              </button>
              <button
                onClick={handleSendTestEmail}
                disabled={!testEmail.trim() || isSendingTest}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 active:bg-blue-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2 font-medium touch-manipulation"
                style={{ minHeight: '48px' }}
              >
                {isSendingTest ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    <span>Send Test</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {kindPicker && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center sm:p-4"
          onClick={() => setKindPicker(null)}
        >
          <div
            className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl max-w-md w-full p-5 sm:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sm:hidden w-10 h-1 bg-gray-300 rounded-full mx-auto mb-4" />
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">What kind of template?</h3>
                <p className="text-xs sm:text-sm text-gray-500">You can change this later.</p>
              </div>
              <button
                onClick={() => setKindPicker(null)}
                className="p-2 hover:bg-gray-100 rounded-xl"
              >
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => chooseKind(false)}
                className="w-full text-left p-4 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50"
              >
                <div className="font-semibold text-gray-900">Saved template</div>
                <div className="text-sm text-gray-500 mt-1">Keep this to use again on campaigns and emails.</div>
              </button>
              <button
                type="button"
                onClick={() => chooseKind(true)}
                className="w-full text-left p-4 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50"
              >
                <div className="font-semibold text-gray-900">One-off</div>
                <div className="text-sm text-gray-500 mt-1">Written for one person. Kept in its own list.</div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Prompt Modal */}
      {showAIPromptModal && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center sm:p-4"
          style={{
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'env(safe-area-inset-bottom)',
            paddingLeft: 'env(safe-area-inset-left)',
            paddingRight: 'env(safe-area-inset-right)'
          }}
          onClick={() => { if (!isAIGenerating) setShowAIPromptModal(false); }}
        >
          <div 
            className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl max-w-lg w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sm:hidden w-10 h-1 bg-gray-300 rounded-full mx-auto mt-3" />
            
            <div className="p-5 sm:p-6">
              <div className="flex items-center space-x-3 mb-5">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Sparkles className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900">Create with AI</h3>
                  <p className="text-xs sm:text-sm text-gray-500">{createOneOff ? 'This will be saved as a one-off.' : 'This will be saved as a reusable template.'}</p>
                </div>
                <button
                  onClick={() => setShowAIPromptModal(false)}
                  className="p-2 hover:bg-gray-100 active:bg-gray-200 rounded-xl transition-colors touch-manipulation"
                >
                  <X className="h-5 w-5 text-gray-400" />
                </button>
              </div>

              <textarea
                ref={aiPromptInputRef}
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                placeholder="e.g. A professional quote follow-up email for a glass repair company with a blue theme..."
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none touch-manipulation"
                rows={4}
                style={{ fontSize: '16px' }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && aiPrompt.trim()) {
                    e.preventDefault();
                    handleAICreate();
                  }
                }}
              />

              <div className="mt-3 flex flex-wrap gap-2">
                {[
                  'Welcome email for new customers',
                  'Booking confirmation',
                  'Promotional offer / sale',
                  'Quote follow-up',
                  'Feedback request',
                ].map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => setAiPrompt(suggestion)}
                    className="px-3 py-1.5 bg-gray-100 hover:bg-blue-50 hover:text-blue-700 text-gray-600 rounded-lg text-xs font-medium transition-colors touch-manipulation"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>

              <div className="mt-5 flex items-center gap-3">
                <button
                  onClick={() => setShowAIPromptModal(false)}
                  className="flex-1 py-3 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 active:bg-gray-100 font-medium text-sm transition-colors touch-manipulation"
                  style={{ minHeight: '48px' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAICreate}
                  disabled={!aiPrompt.trim() || isAIGenerating}
                  className={`flex-1 py-3 rounded-xl font-medium text-sm transition-all flex items-center justify-center space-x-2 touch-manipulation ${
                    aiPrompt.trim() && !isAIGenerating
                      ? 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 shadow-sm'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                  style={{ minHeight: '48px' }}
                >
                  {isAIGenerating ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Generating...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4" />
                      <span>Generate Template</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Token Purchase Modal */}
      <TokenPurchaseModal
        isOpen={showTokenPurchaseModal}
        onClose={() => setShowTokenPurchaseModal(false)}
        businessId={businessId || ''}
        onSuccess={() => {
          setShowTokenPurchaseModal(false);
          toast.success('Tokens added! You can now use AI Create.');
        }}
      />
    </>
  );
}
